import { ethers } from 'ethers';
import React, { useEffect } from 'react';
import Token from '../../../components/tokenList/Token';
import {
    convertBigToSmallDigitWithSpecificDecimal,
    handleKeyDown,
    handleWheel,
    isContractAddress,
} from '../../../utils/commonFunction';

const SendPaymentBasics = ({
    receiver,
    onReceiverChange,
    amount,
    onAmountChange,
    token,
    onTokenChange,
    walletAddress,
    onRecipientContractChange,
}) => {
    const inputClass = 'cpg-input';
    const labelClass =
        'flex items-center gap-1 text-[13px] font-semibold text-zinc-900 dark:text-zinc-200';

    const tokenBalanceFormatted =
        token?.decimals !== undefined
            ? convertBigToSmallDigitWithSpecificDecimal(
                  token.balance,
                  token.decimals,
              )
            : null;

    const isInvalidAddress = receiver && !ethers.isAddress(receiver);
    const isSelfPayment =
        receiver &&
        ethers.isAddress(receiver) &&
        walletAddress &&
        receiver.toLowerCase() === walletAddress.toLowerCase();

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            if (!receiver || !ethers.isAddress(receiver)) {
                onRecipientContractChange?.(false);
                return;
            }
            try {
                const isContract = await isContractAddress(receiver);
                if (!cancelled) onRecipientContractChange?.(isContract);
            } catch (_e) {
                if (!cancelled) onRecipientContractChange?.(false);
            }
        };
        void check();
        return () => {
            cancelled = true;
        };
    }, [receiver, onRecipientContractChange]);

    const onMaxClick = () => {
        if (!token?.decimals || !token?.balance) return;
        const max = convertBigToSmallDigitWithSpecificDecimal(
            token.balance,
            token.decimals,
        );
        onAmountChange(String(max ?? ''));
    };

    return (
        <div className="space-y-5">
            <div>
                <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="send-receiver" className={labelClass}>
                        Receiver Wallet Address{' '}
                        <span className="text-rose-600 dark:text-rose-400">*</span>
                    </label>
                    <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">
                        EVM-compatible address only
                    </span>
                </div>
                <input
                    type="text"
                    id="send-receiver"
                    value={receiver}
                    onChange={(e) => onReceiverChange(e.target.value)}
                    placeholder="0x... (42 characters)"
                    autoComplete="off"
                    spellCheck={false}
                    className={`${inputClass} font-mono ${
                        isInvalidAddress
                            ? 'border-rose-500/50 focus:border-rose-500/50 focus:ring-rose-500/40'
                            : ''
                    }`}
                />
                {isInvalidAddress && (
                    <p className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-300">
                        <i className="fa-solid fa-circle-exclamation text-[10px]" />
                        Address must be a valid 0x… EVM wallet.
                    </p>
                )}
                {isSelfPayment && (
                    <p className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-300">
                        <i className="fa-solid fa-circle-exclamation text-[10px]" />
                        You cannot send a payment to your own wallet.
                    </p>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label className={labelClass}>
                        Token{' '}
                        <span className="text-rose-600 dark:text-rose-400">*</span>
                    </label>
                    <div className="relative mt-2">
                        <Token handleSelectToken={onTokenChange} token={token} />
                    </div>
                </div>
                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <label htmlFor="send-amount" className={labelClass}>
                            Amount{' '}
                            <span className="text-rose-600 dark:text-rose-400">*</span>
                        </label>
                        {token?.name && tokenBalanceFormatted !== null && (
                            <button
                                type="button"
                                onClick={onMaxClick}
                                className="text-[11px] font-bold tabular-nums text-emerald-700 transition-colors hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
                                title="Use max balance"
                            >
                                Max: {tokenBalanceFormatted} {token.symbol || ''}
                            </button>
                        )}
                    </div>
                    <input
                        type="number"
                        id="send-amount"
                        min={0}
                        onWheel={handleWheel}
                        onKeyDown={handleKeyDown}
                        value={amount}
                        onChange={(e) => onAmountChange(e.target.value)}
                        placeholder="0.00"
                        className={`${inputClass} tabular-nums`}
                    />
                </div>
            </div>
        </div>
    );
};

export default SendPaymentBasics;
